import { CustomLink } from '../../components/custom-link';
import { cn } from '../../lib';
import { SanityImage } from '../../sanity/utils/components';
import { useCart } from '../context';
import { useCartItem, useTileCartItem } from '../hooks';
import type { ContextCartItem } from '../types';
import { EditQuantityButton } from './edit-quantity-button';
import { RemoveFromCartButton } from './remove-from-cart-button';

export function CartItem({
  item,
  className,
  baseUrl,
}: {
  item: ContextCartItem;
  className?: string;
  baseUrl: string;
}) {
  const { loading, setOpen } = useCart();
  const { price, originalPrice, hasDiscount, preOrder } = useCartItem(item);

  const { isTile, meteredPrice, formattedQuantity, areaDisplay } =
    useTileCartItem(item);

  return (
    <article
      className={cn(
        'flex items-start border-gray-200 border-b py-6 text-[hsl(var(--color))]',
        className
      )}
    >
      {item.image && (
        <div className="relative block h-24 w-24 self-center rounded-theme-sm">
          <SanityImage
            image={item.image}
            fill
            noLQIP
            sizes="160px"
            className="h-full w-full object-contain"
          />
        </div>
      )}
      <main className="ml-5 flex flex-1 flex-col">
        {item.productUrl ? (
          <h3 className="font-bold text-sm uppercase">
            <CustomLink
              href={item.productUrl}
              onClick={() => setOpen(false)}
              className="hf:underline hf:outline-none outline-none transition-all"
            >
              {item.title}
            </CustomLink>
          </h3>
        ) : (
          <h3 className="font-bold text-sm uppercase">{item.title}</h3>
        )}

        {preOrder.isPreOrder && <h4 className="text-xs">{preOrder.text}</h4>}
        {item.option1 && item.option1 !== 'Default Title' && (
          <h4 className="text-xs">{item.option1}</h4>
        )}
        {item.option2 && <h4 className="text-xs">{item.option2}</h4>}
        {item.option3 && <h4 className="text-xs">{item.option3}</h4>}

        <div className="mt-1 flex items-baseline gap-1.5 font-medium text-xs">
          {hasDiscount ? (
            <>
              <span className="text-theme-text/50 line-through">
                {originalPrice}
              </span>
              <span className="font-bold text-primary-outline">
                {isTile && meteredPrice ? meteredPrice : price}
              </span>
            </>
          ) : null}
          {!hasDiscount && (
            <span className="font-bold text-primary-outline">
              {isTile && meteredPrice ? meteredPrice : price}
            </span>
          )}
        </div>
        <div className="mt-2 flex xs:items-center gap-2 max-xs:flex-col max-xs:items-start">
          <fieldset
            className="flex select-none items-center gap-1.5 rounded border border-[hsl(var(--color))] px-1 font-semibold text-[12px]"
            aria-label="Quantity controls"
          >
            <EditQuantityButton baseUrl={baseUrl} item={item} type="minus" />
            <div className="flex items-center gap-1">
              <output>
                <span className={cn(loading && 'opacity-80')}>
                  {formattedQuantity}
                </span>
              </output>
              {areaDisplay && (
                <span className="font-bold text-xs">{areaDisplay}</span>
              )}
            </div>
            <EditQuantityButton baseUrl={baseUrl} item={item} type="plus" />
          </fieldset>
        </div>
      </main>

      <RemoveFromCartButton
        item={item}
        baseUrl={baseUrl}
        className="ml-auto flex p-1 hf:text-theme-text/80 text-theme-text transition-all"
      />
    </article>
  );
}
